import React from 'react';
import { Row, Col, Form } from 'antd';
import {
  injectStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement
} from 'react-stripe-elements';
import { get, toString, toLower } from 'lodash';
import { connect } from 'react-redux';

import helpers from 'utils/helpers';
import {
  getTermsUrl,
  getPrivacyUrl,
  getProhibitedItemsUrl,
  openPopupWindow
} from 'utils/links';
import {
  orderProcessRequest,
  orderCouponRequest,
  orderRemoveCouponRequest
} from 'ducks/shipments';
import { addPaymentMethodRequest, getPaymentMethodRequest } from 'ducks/users';
import { offerGiftRequest } from 'ducks/campaigns';
import {
  FormTitle,
  StyledInput,
  StyledButton,
  NextButton,
  CardImage,
  CardImageContainer
} from 'components/Styled';
import ImageCard from 'assets/images/cards/card.svg';
import ImageCardInvalid from 'assets/images/cards/card-invalid.svg';
import ImageVisa from 'assets/images/cards/visa.svg';
import ImageMastercard from 'assets/images/cards/mastercard.svg';
import ImageAmex from 'assets/images/cards/amex.svg';
import ImageDiners from 'assets/images/cards/diners.svg';
import ImageDiscover from 'assets/images/cards/discover.svg';
import ImageJcb from 'assets/images/cards/jcb.svg';

const cardImages = {
  visa: ImageVisa,
  mastercard: ImageMastercard,
  amex: ImageAmex,
  diners: ImageDiners,
  discover: ImageDiscover,
  jcb: ImageJcb
};

const savedCardImages = {
  visa: ImageVisa,
  mastercard: ImageMastercard,
  'american express': ImageAmex,
  diners: ImageDiners,
  discover: ImageDiscover,
  jcb: ImageJcb
};

const cardPlaceholders = {
  unknown: ImageCard,
  invalid: ImageCardInvalid
};

class StripeForm extends React.Component {
  state = {
    name: '',
    brand: 'unknown',
    modifyCard: true,
    uiLoaded: false,
    token: null
  };

  handleCouponRequests = () => {
    if (!this.props.currentCoupon.code && this.props.coupon) {
      // add
      this.props.orderCouponRequest({
        orderRef: this.props.orderRef,
        code: this.props.coupon
      });
    } else if (
      this.props.currentCoupon.code &&
      this.props.coupon &&
      this.props.currentCoupon.code !== this.props.coupon
    ) {
      // remove and add
      this.props.orderRemoveCouponRequest({
        orderRef: this.props.orderRef,
        code: this.props.currentCoupon.code
      });
    } else if (this.props.currentCoupon.code && !this.props.coupon) {
      // remove
      this.props.orderRemoveCouponRequest({
        orderRef: this.props.orderRef,
        code: this.props.currentCoupon.code
      });
    } else {
      // nothing
      this.orderProcessRequest();
    }
  };

  orderProcessRequest = (options = {}) => {
    if (options.preRequest) {
      return this.props.orderProcessRequest({
        preRequest: true
      });
    }

    if (options.preRequestError) {
      return this.props.orderProcessRequest({
        preRequestError: true
      });
    }

    if (this.props.canAdjustment || !this.state.modifyCard) {
      return this.props.orderProcessRequest({
        orderRef: this.props.orderRef,
        slug: this.props.paymentMethod,
        token: this.state.token
      });
    }

    this.props.addPaymentMethodRequest({
      paymentType: 'stripe',
      token: this.state.token,
      name: this.state.name
    });
  };

  handleSubmit = e => {
    e.preventDefault();
    if (this.props.memberRefStatus === 'error') return;
    if (!this.props.campaign || !this.props.memberRef) {
      this.handlePayment();
    } else {
      this.props.offerGiftRequest({
        campaignSlug: this.props.campaign,
        orderRef: this.props.orderRef,
        memberRef: this.props.memberRef,
        firstName: this.props.firstName,
        lastName: this.props.lastName
      });
    }
  };

  handlePayment = () => {
    const paymentData = {
      promoCode: this.props.coupon,
      discount: this.props.discount,
      amount:
        this.props.shipmentData?.naturalAmount?.totalRate ||
        this.props.shipmentData?.totalRate,
      currency: 'HKD',
      chargeBreakdownItem: ['freightCharge', 'fuelSurcharge', 'otherCharges'],
      chargeBreakdownValue: [
        this.props.shipmentData?.naturalAmount?.freightCharge ||
          this.props.shipmentData?.freightCharge,
        this.props.shipmentData?.naturalAmount?.fuelSurcharge ||
          this.props.shipmentData?.fuelSurcharge,
        this.props.shipmentData?.naturalAmount?.otherCharges ||
          this.props.shipmentData?.otherCharges
      ]
    };
    const { name } = this.state;

    this.orderProcessRequest({
      preRequest: true
    });

    if (!this.state.modifyCard) {
      const paymentMethod = this.props.paymentMethodDetail;
      paymentData.cardBrand = paymentMethod.brand;
      paymentData.cardNumber = paymentMethod.last4;

      window.dataLayer.push({
        event: 'Submit Payment',
        ecommerce: undefined,
        paymentData
      });

      this.setState({ token: null }, this.handleCouponRequests);
      return;
    }

    this.props.stripe
      .createToken({
        type: 'card',
        name: name
      })
      .then(rs => {
        paymentData.cardNumber = get(rs, 'token.card.last4');
        paymentData.cardBrand = get(rs, 'token.card.brand');

        window.dataLayer.push({
          event: 'Submit Payment',
          ecommerce: undefined,
          paymentData
        });

        let paymentSuccess = true;
        if (rs.error) {
          paymentSuccess = false;
        }

        if (!name) {
          paymentSuccess = false;
          this.setState({
            nameStatus: 'error'
          });
        }

        if (!paymentSuccess) {
          window.dataLayer.push({
            event: 'Payment Error',
            errorData: {
              backendMessage: '',
              frontendMessage: rs.error.message,
              code: rs.error.code
            }
          });

          helpers.message.error(
            this.props.t(`payment.err_msg.${get(rs, 'error.code')}`) ||
              this.props.t('common.err_msg.input'),
            'payment'
          );

          this.orderProcessRequest({
            preRequestError: true
          });

          return;
        }

        this.setState({ token: rs.token.id }, this.handleCouponRequests);
      });
  };

  handleNameChange = e => {
    this.setState({
      name: e.target.value,
      nameStatus: ''
    });
  };

  handleNameBlur = () => {
    window.dataLayer.push({
      event: 'Inputted Payment Name',
      ecommerce: undefined,
      paymentInput: toString(this.state.name)
    });
  };

  handleCardChange = e => {
    this.setState({
      brand: e.error ? 'invalid' : e.brand
    });
  };

  cardInputDiv = React.createRef();

  handleModifyClick = () => {
    window.dataLayer.push({
      event: 'Click Edit Card',
      ecommerce: undefined
    });

    this.setState(
      {
        modifyCard: true
      },
      () => {
        window.scrollTo(0, this.cardInputDiv.current.offsetTop);
      }
    );
  };

  handleTermsClick = e => {
    e.preventDefault();
    const lang = helpers.getCurrentLang();
    const country = helpers.getCurrentCountry(true);
    const url = getTermsUrl(lang, country);
    openPopupWindow(url);
  };

  handlePrivacyClick = e => {
    e.preventDefault();
    const lang = helpers.getCurrentLang();
    const country = helpers.getCurrentCountry(true);
    const url = getPrivacyUrl(lang, country);
    openPopupWindow(url);
  };

  handleProhibitedItemsClick = e => {
    e.preventDefault();
    const lang = helpers.getCurrentLang();
    const country = helpers.getCurrentCountry(true);
    const url = getProhibitedItemsUrl(lang, country);
    openPopupWindow(url);
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    const paymentMethod = this.props.paymentMethodDetail;
    const prevPaymentMethod = prevProps.paymentMethodDetail;

    if (!prevPaymentMethod && paymentMethod && prevState.modifyCard) {
      this.setState({
        modifyCard: false
      });
    }

    if (prevProps.uiLoading && !this.props.uiLoading) {
      this.setState({
        uiLoaded: true
      });
    }

    if (!prevProps.offerGiftSuccess && this.props.offerGiftSuccess)
      this.handlePayment();

    if (!prevProps.couponSuccess && this.props.couponSuccess) {
      this.orderProcessRequest();
    }

    if (!prevProps.couponRemoveSuccess && this.props.couponRemoveSuccess) {
      if (this.props.coupon) {
        this.props.orderCouponRequest({
          orderRef: this.props.orderRef,
          code: this.props.coupon
        });
      } else {
        this.orderProcessRequest();
      }
    }

    if (!prevProps.addPaymentMethodFail && this.props.addPaymentMethodFail) {
      this.orderProcessRequest({
        preRequestError: true
      });
    }

    if (
      !prevProps.addPaymentMethodSuccess &&
      this.props.addPaymentMethodSuccess
    ) {
      this.props.getPaymentMethodRequest({
        paymentType: 'stripe',
        user: this.props.user
      });

      return this.props.orderProcessRequest({
        orderRef: this.props.orderRef,
        slug: this.props.paymentMethod
      });
    }
  }

  componentDidMount() {
    this.props.getPaymentMethodRequest({
      paymentType: 'stripe',
      user: this.props.user
    });
  }

  render() {
    if (!this.state.uiLoaded) {
      return null;
    }

    const style = {
      base: {
        color: '#2C2C2E',
        fontSize: '14px',
        fontFamily: `'Lato', 'Gensen Rounded', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
        sans-serif`,
        fontSmoothing: 'antialiased',
        lineHeight: '1.5',
        '::placeholder': {
          color: '#bfbfbf'
        }
      },
      invalid: {
        color: '#2C2C2E'
      }
    };

    // currently only return 1 card object
    const paymentMethod = this.props.paymentMethodDetail;

    return (
      <React.Fragment>
        <form onSubmit={this.handleSubmit}>
          <div
            ref={this.cardInputDiv}
            style={{
              display:
                !paymentMethod || this.state.modifyCard ? 'block' : 'none'
            }}>
            <FormTitle>{this.props.t('payment.form.name')}</FormTitle>
            <Form.Item
              style={{ margin: 0 }}
              validateStatus={this.state.nameStatus || ''}>
              <StyledInput
                size="large"
                value={this.state.name}
                onChange={this.handleNameChange}
                onBlur={this.handleNameBlur}
              />
            </Form.Item>
            <FormTitle style={{ paddingTop: '20px' }}>
              {this.props.t('payment.form.credit_card')}
            </FormTitle>
            <div style={{ position: 'relative' }}>
              <CardImageContainer>
                <CardImage
                  src={cardImages[this.state.brand]}
                  style={{
                    zIndex: 1,
                    transform:
                      this.state.brand === 'unknown' ||
                      this.state.brand === 'invalid'
                        ? 'scale(.7)'
                        : 'scale(1)',
                    opacity:
                      this.state.brand === 'unknown' ||
                      this.state.brand === 'invalid'
                        ? 0
                        : 1
                  }}
                  alt=""
                />
                <CardImage
                  src={
                    cardPlaceholders[this.state.brand] ||
                    cardPlaceholders.unknown
                  }
                  style={{
                    transform:
                      this.state.brand === 'unknown' ||
                      this.state.brand === 'invalid'
                        ? 'scale(1)'
                        : 'scale(.7)',
                    opacity:
                      this.state.brand === 'unknown' ||
                      this.state.brand === 'invalid'
                        ? 1
                        : 0
                  }}
                  alt=""
                />
              </CardImageContainer>
              <CardNumberElement
                className="ant-input CardNumberElement"
                style={style}
                placeholder=""
                onChange={this.handleCardChange}
              />
            </div>
            <Row type="flex" gutter={14} style={{ paddingTop: '20px' }}>
              <Col span={12}>
                <FormTitle>
                  {this.props.t('payment.form.expiry_date')}
                </FormTitle>
                <CardExpiryElement className="ant-input" style={style} />
              </Col>
              <Col span={12}>
                <FormTitle>{this.props.t('payment.form.cvc')}</FormTitle>
                <CardCVCElement
                  className="ant-input"
                  style={style}
                  placeholder=""
                />
              </Col>
            </Row>
          </div>
          {!paymentMethod || this.state.modifyCard ? null : (
            <div
              style={{
                boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.2)',
                backgroundColor: '#fff',
                padding: '26px 23px',
                alignItems: 'center',
                display: 'flex'
              }}>
              <div style={{ flex: '0 0 auto' }}>
                <img
                  style={{ width: '35px' }}
                  src={
                    savedCardImages[toLower(paymentMethod['brand'])] ||
                    ImageCard
                  }
                  alt=""
                />
              </div>
              <div
                style={{
                  flex: '1 0 auto',
                  padding: '0 23px',
                  fontSize: '16px'
                }}>{`xxxx xxxx xxxx ${paymentMethod['last4']}`}</div>
              <div
                className="buttonThemeHover-color"
                style={{
                  flex: '0 0 auto',
                  color: '#9900A5',
                  cursor: 'pointer'
                }}
                onClick={this.handleModifyClick}>
                {this.props.t('button.edit')}
              </div>
            </div>
          )}
          <Row
            type="flex"
            gutter={14}
            style={{
              paddingTop: '20px',
              color: '#2C2C2E',
              lineHeight: '17px',
              fontSize: '12px'
            }}>
            <Col span={24}>
              {this.props.t('payment.agreement')}
              <span style={{ fontWeight: 'bold' }}>
                {this.props.t('payment.agree_and_pay')}
              </span>
              <span>{this.props.t('payment.agreement_1')}</span>
              <a
                className="buttonSecond-color"
                style={{
                  fontWeight: 'bold',
                  color: '#9900A5',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                href=""
                onClick={this.handleTermsClick}>
                {this.props.t('payment.terms')}
              </a>
              <span>{this.props.t('payment.agreement_2')}</span>
              <a
                className="buttonSecond-color"
                style={{
                  fontWeight: 'bold',
                  color: '#9900A5',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                href=""
                onClick={this.handlePrivacyClick}>
                {this.props.t('payment.privacy')}
              </a>
              <span>{this.props.t('payment.agreement_3')}</span>
              <a
                className="buttonSecond-color"
                style={{
                  fontWeight: 'bold',
                  color: '#9900A5',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                href=""
                onClick={this.handleProhibitedItemsClick}>
                {this.props.t('payment.prohibited')}
              </a>
              <span>{this.props.t('payment.agreement_4')}</span>
            </Col>
          </Row>
          <div style={{ padding: '28px 0 20px' }}>
            <NextButton
              block
              step="true"
              type="primary"
              height={60}
              onClick={this.handleSubmit}
              loading={this.props.loading || this.props.uiLoading}>
              {this.props.t('payment.agree_and_pay')}
            </NextButton>
          </div>
        </form>
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => {
  return {
    loading: !!state.shipments.loading,
    user: state.auth.user || false,
    pickupData: state.shipments.pickupData || {},
    shipmentData: state.shipments.shipmentData || {},
    processSuccess: !!state.shipments.processSuccess,
    paymentMethodDetail: state.users.paymentMethodDetail || false,
    couponSuccess: !!state.shipments.couponSuccess,
    couponRemoveSuccess: !!state.shipments.couponRemoveSuccess,
    addPaymentMethodSuccess: !!state.users.addPaymentMethodSuccess,
    addPaymentMethodFail: !!state.users.addPaymentMethodFail,
    currentCoupon: state.shipments.coupon || {},
    uiLoading: !!state.users.loading,
    offerGiftSuccess: state.campaigns.offerGiftSuccess || false
  };
};

const mapDispatchToProps = {
  getPaymentMethodRequest,
  orderProcessRequest,
  orderCouponRequest,
  orderRemoveCouponRequest,
  addPaymentMethodRequest,
  offerGiftRequest
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectStripe(StripeForm));
